import React from 'react';
import { Navigate, Outlet, useParams, useLocation } from 'react-router-dom';
import { getUser } from '../services/auth';

// First path segments that are app routes, not tenant codes. Old URLs like /client/123 need to become /:tenant/client/123.
export const APP_PATH_SEGMENTS = new Set([
  'lists', 'clients', 'client', 'providers', 'provider', 'payees', 'payee', 'contacts', 'contact',
  'schools', 'school', 'reports', 'referrals', 'referral', 'dashboard', 'work-tasks', 'settings', 'super-admin', 'login', 'transfers',
  'client-services', 'at-packets', 'file-reviews', 'my-checklists', 'view-as', 'cornerstone'
]);

/**
 * Renders under RequireAuth when route is /:tenantCode/...
 * Validates that the URL tenant is one the user has access to; otherwise redirects to their session tenant.
 * If the first segment is an app path (e.g. "client" from old URL /client/123), redirects to /:sessionTenant/client/123.
 */
function TenantLayout() {
  const { tenantCode } = useParams();
  const location = useLocation();
  const user = getUser();
  const sessionTenant = user?.tenant?.code;
  const memberships = user?.memberships || [];

  // Legacy URL: e.g. /super-admin or /client/123 → redirect to /:sessionTenant/... so first segment is real tenant, not app path
  if (sessionTenant && tenantCode && APP_PATH_SEGMENTS.has(tenantCode)) {
    const pathname = location.pathname || '';
    const pathAfterFirstSegment = pathname.replace(/^\/[^/]+/, '') || '';
    const target = `/${sessionTenant}${pathAfterFirstSegment || '/dashboard'}`;
    return <Navigate to={target} replace />;
  }

  const hasAccess =
    sessionTenant === tenantCode ||
    memberships.some((m) => (m.tenant_code || m.tenantCode) === tenantCode);

  if (!tenantCode || !sessionTenant) {
    return <Outlet />;
  }
  if (!hasAccess) {
    return <Navigate to={`/${sessionTenant}/dashboard`} replace />;
  }
  return <Outlet />;
}

export default TenantLayout;
